import type { LifeSpace, Project } from '@/src/renderer/lib/db/models'
import { flattenProjectTree } from '@/src/renderer/lib/projectTree'
import { emptyTables, isDeviceSettingKey, LIFE_SPACES, type BackupTables } from './format'

/**
 * **Die Auswahl als Baum** (#158, Entscheidung 1): Lebensbereich → Projekte (Unterprojekte ziehen
 * mit), „Kurz notiert“, Ziele, Gewohnheiten; dazu „Einstellungen“ und — nur beim Export — „Profil“.
 * Dieselbe Auswahl gilt beim Backup erstellen, Wiederherstellen und Exportieren.
 *
 * Gewählt sind **Blätter** (`project:<id>`, `quickNotes:<bereich>`, …); Lebensbereich und
 * Elternprojekt rechnen ihren Haken daraus (an, aus, teilweise).
 */
export type SelectionKind = 'lifeSpace' | 'project' | 'quickNotes' | 'goals' | 'habits' | 'settings' | 'profile'

export interface SelectionNode {
  /** Eindeutig im Baum; bei allem außer dem Lebensbereich zugleich der Schlüssel in der Auswahl. */
  key: string
  kind: SelectionKind
  lifeSpace?: LifeSpace
  /** Projektname; die übrigen Knoten beschriftet die Oberfläche. */
  name?: string
  /** Projekt-Icon und seine Farbe (`Project.icon`, `Project.iconColor`). */
  icon?: string | null
  iconColor?: string | null
  /** Aufgaben im Projekt bzw. in „Kurz notiert“, Ziele, Gewohnheiten, Einstellungen; beim Lebensbereich alles darunter. */
  count: number
  /** `false`: gibt es nicht (in dieser Datei) — ausgegraut, nicht wählbar. */
  available: boolean
  children: SelectionNode[]
}

export type SelectionState = 'checked' | 'unchecked' | 'partial'

export const projectKey = (id: string) => `project:${id}`

/**
 * Baut den Baum aus den Zeilen (lebende; Gelöschtes zählt nicht). `profile` nur beim Export.
 */
export function buildSelectionTree(tables: BackupTables, options: { profile?: boolean; hasProfile?: boolean } = {}): SelectionNode[] {
  const live = <T extends { deletedAt?: Date | null }>(rows: readonly T[]) => rows.filter(row => !row.deletedAt)
  const tasks = live(tables.tasks)
  const tasksByProject = new Map<string, number>()
  const quickNotes = new Map<LifeSpace, number>()
  for (const task of tasks) {
    if (task.projectId) tasksByProject.set(task.projectId, (tasksByProject.get(task.projectId) ?? 0) + 1)
    else quickNotes.set(task.lifeSpace, (quickNotes.get(task.lifeSpace) ?? 0) + 1)
  }

  const nodes: SelectionNode[] = LIFE_SPACES.map(lifeSpace => {
    const projects = live(tables.projects).filter(p => p.lifeSpace === lifeSpace && !p.isSystem)
    const children: SelectionNode[] = [
      ...projectNodes(projects, tasksByProject, lifeSpace),
      leaf(`quickNotes:${lifeSpace}`, 'quickNotes', lifeSpace, quickNotes.get(lifeSpace) ?? 0),
      leaf(`goals:${lifeSpace}`, 'goals', lifeSpace, live(tables.goals).filter(g => g.lifeSpace === lifeSpace).length),
      leaf(`habits:${lifeSpace}`, 'habits', lifeSpace, live(tables.habits).filter(h => h.lifeSpace === lifeSpace).length),
    ]
    const count = children.reduce((sum, child) => sum + subtreeCount(child), 0)
    return {
      key: `lifeSpace:${lifeSpace}`, kind: 'lifeSpace', lifeSpace, count,
      available: children.some(isSelectable), children,
    }
  })

  const settingsCount = tables.settings.filter(s => !isDeviceSettingKey(s.key)).length
    + live(tables.userPreferences).length + live(tables.rewardUsages).length
  nodes.push(leaf('settings', 'settings', undefined, settingsCount))
  if (options.profile) nodes.push({ ...leaf('profile', 'profile', undefined, options.hasProfile ? 1 : 0) })
  return nodes
}

function leaf(key: string, kind: SelectionKind, lifeSpace: LifeSpace | undefined, count: number): SelectionNode {
  return { key, kind, ...(lifeSpace ? { lifeSpace } : {}), count, available: count > 0, children: [] }
}

/** Projekte als Baum in der Reihenfolge der Sidebar; ein Projekt gibt es auch leer. */
function projectNodes(projects: Project[], tasksByProject: Map<string, number>, lifeSpace: LifeSpace): SelectionNode[] {
  const byId = new Map<string, SelectionNode>()
  const roots: SelectionNode[] = []
  for (const row of flattenProjectTree(projects)) {
    const node: SelectionNode = {
      key: projectKey(row.id), kind: 'project', lifeSpace, name: row.project.name,
      icon: row.project.icon ?? null, iconColor: row.project.iconColor ?? null,
      count: tasksByProject.get(row.id) ?? 0, available: true, children: [],
    }
    byId.set(row.id, node)
    const parent = row.parentId ? byId.get(row.parentId) : undefined
    if (parent) parent.children.push(node)
    else roots.push(node)
  }
  return roots
}

function subtreeCount(node: SelectionNode): number {
  return node.kind === 'project' ? node.count + node.children.reduce((sum, child) => sum + subtreeCount(child), 0) : node.count
}

const isSelectable = (node: SelectionNode): boolean => node.available || node.children.some(isSelectable)

/** Die wählbaren Schlüssel unter einem Knoten, ihn selbst eingeschlossen. */
export function selectableKeys(node: SelectionNode): string[] {
  const own = node.kind !== 'lifeSpace' && node.available ? [node.key] : []
  return [...own, ...node.children.flatMap(selectableKeys)]
}

/** Alle wählbaren Schlüssel des Baums — die Vorgabe „alles gewählt“. */
export function allSelectableKeys(tree: readonly SelectionNode[]): Set<string> {
  return new Set(tree.flatMap(selectableKeys))
}

export function nodeState(node: SelectionNode, selected: ReadonlySet<string>): SelectionState {
  const keys = selectableKeys(node)
  if (keys.length === 0) return 'unchecked'
  const chosen = keys.filter(key => selected.has(key)).length
  return chosen === 0 ? 'unchecked' : chosen === keys.length ? 'checked' : 'partial'
}

/** Haken setzen oder nehmen: Ein voller Knoten wird leer, sonst wird er voll — samt allem darunter. */
export function toggleNode(node: SelectionNode, selected: ReadonlySet<string>): Set<string> {
  const next = new Set(selected)
  const keys = selectableKeys(node)
  if (nodeState(node, selected) === 'checked') keys.forEach(key => next.delete(key))
  else keys.forEach(key => next.add(key))
  return next
}

/** Nimmt einen Knoten aus der Auswahl — etwa „Einstellungen“ beim Wiederherstellen, solange der Haken aus ist. */
export function withoutKey(selected: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(selected)
  next.delete(key)
  return next
}

/**
 * **Auswahl → Zeilen.** Ohne Haken am Lebensbereich ist er samt Inhalt draußen. Tags gehen mit,
 * wenn eine gewählte Aufgabe sie trägt; ist alles gewählt (`allTags`), gehen alle mit.
 * Gelöschte Zeilen bleiben, wie sie sind — gefiltert wird nur nach Ort.
 */
export function selectRows(tables: BackupTables, selected: ReadonlySet<string>, options: { allTags?: boolean } = {}): BackupTables {
  const projectIds = new Set(tables.projects.filter(p => selected.has(projectKey(p.id))).map(p => p.id))
  const tasks = tables.tasks.filter(task => (task.projectId
    ? projectIds.has(task.projectId)
    : selected.has(`quickNotes:${task.lifeSpace}`)))
  const taskIds = new Set(tasks.map(task => task.id))
  const habits = tables.habits.filter(h => selected.has(`habits:${h.lifeSpace}`))
  const habitIds = new Set(habits.map(h => h.id))
  const usedTags = new Set(tasks.flatMap(task => task.tags))
  const withSettings = selected.has('settings')

  return {
    ...emptyTables(),
    tags: options.allTags ? tables.tags : tables.tags.filter(tag => usedTags.has(tag.id)),
    projects: tables.projects.filter(p => projectIds.has(p.id)),
    sections: tables.sections.filter(s => projectIds.has(s.projectId)),
    tasks,
    canvasNotes: tables.canvasNotes.filter(n => projectIds.has(n.projectId)),
    taskComments: tables.taskComments.filter(c => taskIds.has(c.taskId)),
    projectComments: tables.projectComments.filter(c => projectIds.has(c.projectId)),
    taskReminders: tables.taskReminders.filter(r => taskIds.has(r.taskId)),
    timeEntries: tables.timeEntries.filter(e => taskIds.has(e.taskId)),
    goals: tables.goals.filter(g => selected.has(`goals:${g.lifeSpace}`)),
    habits,
    habitLogs: tables.habitLogs.filter(l => habitIds.has(l.habitId)),
    settings: withSettings ? tables.settings.filter(s => !isDeviceSettingKey(s.key)) : [],
    userPreferences: withSettings ? tables.userPreferences : [],
    rewardUsages: withSettings ? tables.rewardUsages : [],
  }
}

/** Alles, wie „Backup erstellen“ mit allen Haken — so sieht auch das automatische Backup aus. */
export function selectEverything(tables: BackupTables): BackupTables {
  return selectRows(tables, new Set([...allSelectableKeys(buildSelectionTree(tables)), 'settings']), { allTags: true })
}

/** Ob jeder wählbare Inhalt gewählt ist — dann gehen alle Tags mit. */
export function selectsEverything(tree: readonly SelectionNode[], selected: ReadonlySet<string>): boolean {
  return tree.filter(node => node.kind === 'lifeSpace').every(node => selectableKeys(node).every(key => selected.has(key)))
}

/**
 * **Auswahl auf frische Daten übertragen**: Zwischen Öffnen und Klick können Projekte dazukommen. Ein
 * Lebensbereich, der ganz gewählt war, nimmt sie mit — „ein Haken am Lebensbereich nimmt alles
 * darunter mit“; sonst bleiben die gewählten Schlüssel, die es noch gibt.
 */
export function carrySelection(before: readonly SelectionNode[], selected: ReadonlySet<string>, after: readonly SelectionNode[]): Set<string> {
  const known = allSelectableKeys(after)
  const next = new Set([...selected].filter(key => known.has(key)))
  for (const node of after) {
    const old = before.find(o => o.key === node.key)
    if (node.kind === 'lifeSpace' && old && nodeState(old, selected) === 'checked') selectableKeys(node).forEach(key => next.add(key))
  }
  return next
}
